import React from "react";
import { Linkedin, Instagram, Facebook, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/landing/Nav";

export function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[var(--navy)] text-[var(--ivory)] border-t border-[rgba(201,168,76,0.18)]"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="grid lg:grid-cols-[1.2fr_1fr_1fr_1fr] gap-12 lg:gap-10">
          <div>
            <Logo />
            <p className="mt-6 font-display italic text-[1.15rem] text-[var(--gold)]/90">
              Where intelligent investment begins.
            </p>
            <a
              href="mailto:info@ascendpropertygroup.co.uk"
              data-testid="footer-email"
              className="mt-6 inline-flex items-center gap-2 text-[var(--ivory)]/75 hover:text-[var(--gold)] transition-colors text-sm"
            >
              <Mail size={16} /> info@ascendpropertygroup.co.uk
            </a>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <a
                href="https://instagram.com/ascendpropertyuk"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="social-instagram"
                className="w-10 h-10 border border-[rgba(201,168,76,0.35)] rounded-full flex items-center justify-center text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--navy)] transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                href="https://www.facebook.com/ascendpropertygroup"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook — Ascend Property Group"
                data-testid="social-facebook-group"
                className="w-10 h-10 border border-[rgba(201,168,76,0.35)] rounded-full flex items-center justify-center text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--navy)] transition-colors"
              >
                <Facebook size={16} />
              </a>
              <a
                href="https://www.facebook.com/AscendBuysHouses"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook — Ascend Buys Houses"
                data-testid="social-facebook-buys"
                className="w-10 h-10 border border-[rgba(201,168,76,0.35)] rounded-full flex items-center justify-center text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--navy)] transition-colors"
              >
                <Facebook size={16} />
              </a>
              <a
                href="https://www.linkedin.com/company/ascend-property"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="social-linkedin"
                className="w-10 h-10 border border-[rgba(201,168,76,0.35)] rounded-full flex items-center justify-center text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--navy)] transition-colors"
              >
                <Linkedin size={16} />
              </a>
            </div>
          </div>

          <FootCol
            title="Navigation"
            links={[
              { href: "/#about", label: "About" },
              { href: "/#strategy", label: "Strategy" },
              { href: "/#why-us", label: "Why Us" },
              { href: "/#guide", label: "Get the Guide" },
            ]}
          />
          <FootCol
            title="Strategies"
            links={[
              { href: "/#strategy", label: "BRRR" },
              { href: "/#strategy", label: "Flips" },
              { href: "/#strategy", label: "Buy-to-Let" },
            ]}
          />
          <FootCol
            title="Legal"
            links={[
              { href: "/privacy-policy", label: "Privacy & Cookies Policy", internal: true },
            ]}
          />
        </div>

        <div className="gold-rule my-12" />

        <p className="text-[0.78rem] leading-relaxed text-[var(--ivory)]/55 max-w-3xl">
          This website does not constitute financial or investment advice. Property
          investment involves risk. Returns are not guaranteed and capital is at risk.
          All projections are estimates and for illustrative purposes only. Please seek
          independent financial advice.
        </p>
        <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[0.78rem] text-[var(--ivory)]/45">
          <span data-testid="footer-copyright">
            © {new Date().getFullYear()} Ascend Property Group. All rights reserved.
          </span>
          <span>
            Registered in England &amp; Wales · Company No. 14038279
          </span>
        </div>
      </div>
    </footer>
  );
}

function FootCol({ title, links }) {
  return (
    <div>
      <p className="text-[0.7rem] tracking-[0.28em] uppercase text-[var(--gold)] mb-5">
        {title}
      </p>
      <ul className="space-y-3">
        {links.map((l, i) => (
          <li key={`${title}-${i}`}>
            {l.internal ? (
              <Link
                to={l.href}
                className="text-[var(--ivory)]/75 hover:text-[var(--gold)] transition-colors text-[0.92rem]"
              >
                {l.label}
              </Link>
            ) : (
              <a
                href={l.href}
                className="text-[var(--ivory)]/75 hover:text-[var(--gold)] transition-colors text-[0.92rem]"
              >
                {l.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
